// Per-hub curated navigation — the "Hubs" model (a redesign follow-up to the
// universal GTM spine in rail-nav.ts).
//
// A Hub is a NAV LENS, not a wall. Selecting a hub swaps the left rail to a
// curated set of destinations for that role and navigates to the hub's landing
// page — but it never touches real grants. Anything a hub omits stays reachable:
//   • ⌘K (CommandPalette enumerates the FULL granted set, not the hub subset)
//   • the "More views you can access" disclosure at the foot of each rail
//   • for admins, the "All destinations (advanced)" option that renders the
//     original rail-nav spine (nothing is ever stranded by curation).
// That escape-hatch design is what keeps this from becoming a siloed
// "switchboard" (the anti-pattern the role-based-redesign-plan rejected).
//
// Leaves reuse RailLeaf + the SAME module gates as rail-nav, so a viewer only
// ever sees hub leaves they're granted (e.g. a custom viewer without `pipeline`
// sees the Sales hub minus its Pipeline row). Hub membership is resolved from
// the viewer's (role, modules) via `detectHub`, which mirrors HomePage's
// persona detection exactly so the rail and the /home composition always agree.
//
// ONE INTENTIONAL GRANT GAP (documented, not a bug): the cs persona does not
// hold `overview`, so the CS → "Usage" row renders for admins (who bypass
// gates) but is absent for a real scoped viewer until that grant is added.
// (The marketing persona DOES hold `milestones` now — personas.ts — so its
// Spend Coverage row renders for real marketing viewers.)

import type { Role } from './auth-types';
import type { ViewAs } from './view-as';
import { getViewAs } from './view-as';
import { RAIL_SECTIONS, RAIL_TAIL, canSeeNav, type RailLeaf } from './rail-nav';

export interface HubGroup {
  /** Section header; omit for the ungrouped primary block. */
  label?: string;
  leaves: RailLeaf[];
}

export interface HubDef {
  key: ViewAs;
  /** Switcher label. */
  label: string;
  /** One-line description shown in the switcher dropdown. */
  blurb: string;
  /** Route navigated to when this hub is selected. */
  landing: string;
  /**
   * Curated groups, rendered top→bottom BELOW the Home/Overview anchor rows
   * (which RailShell renders for every hub). Pulse is always the last group.
   */
  groups: HubGroup[];
  /**
   * Whether the Pinned/Recents ACCOUNT shortcuts (recently-viewed /orgs/:id
   * pages) belong on this hub's rail. True for account-centric personas
   * (Sales/CS/BDR/Exec, who work individual accounts); omitted for the
   * aggregate hubs (Marketing/Product), where per-account shortcuts are noise.
   */
  accountCentric?: boolean;
}

// Pulse — universal, ungated; the last row of every hub.
const PULSE: RailLeaf = { to: '/pulse', label: 'Pulse' };
const companyGroup = (): HubGroup => ({ label: 'Company', leaves: [PULSE] });

export const HUBS: Record<ViewAs, HubDef> = {
  marketing: {
    key: 'marketing',
    label: 'Marketing',
    blurb: 'Demand-gen: spend efficiency, channel/content/influencer attribution, funnel-top.',
    landing: '/paid-ads',
    groups: [
      {
        // The /paid-ads command center: Performance is the campaign
        // scoreboard + what-shipped strip. Other leaves are distinct jobs
        // (Google reconciliation, channel/page attribution), not a second
        // copy of the same spend numbers.
        label: 'Demand gen',
        leaves: [
          {
            to: '/paid-ads?view=performance',
            label: 'Performance',
            module: 'paid_ads',
            keywords: ['search scorecard', 'spend', 'campaigns', 'ctr', 'what shipped'],
          },
          { to: '/paid-ads?view=conversions', label: 'Conversions vs Google', module: 'paid_ads' },
          { to: '/paid-ads?view=channels', label: 'Sources', module: 'acquisition' },
          // Signups & prompt fills per campaign (incl. paid search), by
          // day/week/month — the marketing team's campaign-volume view.
          { to: '/paid-ads?view=campaigns', label: 'Campaigns', module: 'acquisition' },
          // The oppref-token channel view — OpenAI ad clicks are invisible to
          // the channel/campaign views (no UTMs, no referrer), so this is the
          // ONLY place that traffic and its CAPI conversion pipeline show up.
          { to: '/paid-ads?view=openaiads', label: 'OpenAI Ads', module: 'acquisition' },
          // First-touch page funnel incl. the per-page prompt-fills column
          // (the "/flows numbers" ask) — rail-visible, not tab-strip-only.
          { to: '/paid-ads?view=landing', label: 'Landing pages', module: 'acquisition' },
          // The affiliate/partner program (link builder + /flows spotlight +
          // per-partner attribution) — its own page since #313.
          { to: '/influencers', label: 'Influencers', module: 'acquisition' },
        ],
      },
      {
        label: 'Down-funnel',
        leaves: [
          { to: '/activation', label: 'Activation Funnel', module: 'activation' },
          // Marketing owns experiment readouts too (already holds `activation`),
          // and keeps the onboarding ladder itself alongside.
          { to: '/onboarding', label: 'Onboarding', module: 'activation' },
          { to: '/experiments', label: 'Experiments', module: 'activation' },
          // Deep-links the Spend Coverage payback report (the marketing persona
          // holds `milestones` — see personas.ts).
          { to: '/milestones', label: 'Spend Coverage', module: 'milestones' },
        ],
      },
      {
        // The marketing persona holds `insights` + `cohorts` (personas.ts) but
        // the hub curated neither — they were reachable only via ⌘K / More
        // views. Surface them where the persona's grants say they belong.
        label: 'Analysis',
        leaves: [
          { to: '/insights', label: 'Insights', module: 'insights' },
          { to: '/cohorts', label: 'Cohorts', module: 'cohorts' },
        ],
      },
      companyGroup(),
    ],
  },
  product: {
    key: 'product',
    label: 'Product',
    blurb: 'The self-serve analyst: activation funnel, FTUE replays, adoption, stickiness.',
    landing: '/onboarding',
    groups: [
      {
        leaves: [
          { to: '/onboarding', label: 'Onboarding', module: 'activation' },
          { to: '/experiments', label: 'Experiments', module: 'activation' },
          { to: '/activation', label: 'Activation Funnel', module: 'activation' },
          { to: '/ftue', label: 'FTUE Replays', module: 'ftue' },
          { to: '/usage', label: 'Usage', module: 'overview' },
          { to: '/insights', label: 'Insights', module: 'insights' },
          { to: '/cohorts', label: 'Cohorts', module: 'cohorts' },
        ],
      },
      companyGroup(),
    ],
  },
  cs: {
    key: 'cs',
    label: 'Customer Success',
    blurb: 'Retain + engage existing customers: account health, churn risk, NRR.',
    landing: '/home',
    accountCentric: true,
    groups: [
      {
        leaves: [
          { to: '/orgs', label: 'Accounts', module: 'orgs' },
          { to: '/customers', label: 'Customers', module: 'orgs' },
          // Lead-only (module `pipeline`) — CS Managed/Scaled + Sales deals on
          // the same board Sales uses; absent for IC CS reps.
          { to: '/pipeline', label: 'Pipeline', module: 'pipeline' },
          { to: '/retention', label: 'Retention', module: 'retention' },
          // Adoption = the leading churn signal (grant gap: `overview`).
          { to: '/usage', label: 'Usage', module: 'overview' },
          { to: '/qbr/risks', label: 'QBR risks', module: 'orgs' },
        ],
      },
      companyGroup(),
    ],
  },
  sales: {
    key: 'sales',
    label: 'Sales',
    blurb: 'Work your owned accounts and close deals. Pipeline and Contracts are on the rail for reps and leads; leads additionally get team-book reach.',
    landing: '/home',
    accountCentric: true,
    groups: [
      {
        leaves: [
          { to: '/sales/prospecting', label: 'Prospecting', module: 'sales' },
          // Gated on module `pipeline` — granted to sales_rep and sales_lead
          // (CS IC still omits it; see cs hub above).
          { to: '/pipeline', label: 'Pipeline', module: 'pipeline' },
          { to: '/contracts', label: 'Contracts', module: 'pipeline' },
          { to: '/orgs', label: 'Accounts', module: 'orgs' },
          { to: '/forecast', label: 'Forecast', module: 'forecast' },
          { to: '/sales-enablement', label: 'Sales Enablement', module: 'orgs' },
          {
            to: '/sales/pricing',
            label: 'Pricing',
            module: 'orgs',
            keywords: ['pricing', 'quote', 'discount', 'proposal', 'pack', 'seats', 'list price'],
          },
        ],
      },
      companyGroup(),
    ],
  },
  bdr: {
    key: 'bdr',
    label: 'BDR / Prospecting',
    blurb: 'Work the unowned buying-intent queue and book meetings.',
    landing: '/prospect',
    accountCentric: true,
    groups: [
      {
        leaves: [
          { to: '/prospect', label: 'Queue', module: 'prospect' },
          { to: '/orgs', label: 'Accounts', module: 'orgs' },
        ],
      },
      companyGroup(),
    ],
  },
  admin: {
    key: 'admin',
    label: 'Exec / Admin',
    blurb: 'North-star oversight across every hub and all accounts.',
    landing: '/',
    accountCentric: true,
    groups: [
      {
        label: 'Business north-stars',
        leaves: [
          { to: '/milestones', label: 'Milestones', module: 'milestones' },
          { to: '/retention', label: 'Retention', module: 'retention' },
          { to: '/insights', label: 'Insights', module: 'insights' },
        ],
      },
      {
        // Leadership field-target list. adminOnly so a View-as preview of
        // another hub never sprouts this leaf, and canSeeNav hides it from
        // every non-admin. Not the Accounts/Customers org book.
        label: 'Field',
        leaves: [
          {
            to: '/field-plan',
            label: 'Field Plan',
            adminOnly: true,
            keywords: ['global accounts', 'targets', 'use cases', 'max', 'kartik', 'field partners', 'door openers', 'ey'],
          },
        ],
      },
      {
        label: 'Admin & setup',
        leaves: [
          { to: '/tools', label: 'Tools', module: 'tools' },
          { to: '/settings', label: 'Settings', adminOnly: true },
        ],
      },
      companyGroup(),
    ],
  },
};

// Switcher order for the admin dropdown (personas first, Exec last) — mirrors
// the view-as chip order in the mockup.
export const HUB_ORDER: ViewAs[] = ['marketing', 'product', 'cs', 'sales', 'bdr', 'admin'];

// The admin-only escape hatch: renders the original rail-nav spine so no
// destination is ever stranded by curation.
export const ALL_HUB = 'all' as const;
export type HubSel = ViewAs | typeof ALL_HUB;

/**
 * The hub a viewer belongs to, or 'custom' when their grants match no persona.
 * Mirrors HomePage.tsx's persona detection ORDER exactly (bdr → marketing →
 * product → cs → sales) so the curated rail and the /home composition never
 * disagree. Subset-based (not exact-set) so it's robust to future grant tweaks.
 */
export function detectHub(role: Role | null, modules: string[]): ViewAs | 'custom' {
  if (role === 'admin') return 'admin';
  if (role === null) return 'custom';
  // editor + viewer are both gated like non-admins, and HomePage routes BOTH
  // purely by modules (it only special-cases admin) — so detect by modules here
  // too, keeping the rail ↔ /home composition in agreement for editors as well.
  const m = new Set(modules);
  if (m.has('prospect')) return 'bdr';
  if ((m.has('paid_ads') || m.has('acquisition')) && !m.has('orgs')) return 'marketing';
  if (
    m.has('activation') &&
    !m.has('orgs') &&
    !m.has('paid_ads') &&
    !m.has('acquisition')
  )
    return 'product';
  if (m.has('orgs') && m.has('retention')) return 'cs';
  if (m.has('orgs')) return 'sales';
  return 'custom';
}

export interface HubResolution {
  /** 'hub' → render `hub`'s curated groups; 'legacy' → render the rail-nav spine. */
  mode: 'hub' | 'legacy';
  hub?: HubDef;
  /** Currently-selected key (drives the switcher label). */
  active: HubSel;
  /** Dropdown options; empty when the control is a static badge. */
  options: HubSel[];
  canSwitch: boolean;
  /** Static-badge text for non-switchers (e.g. "Customer Success hub", "Custom"). */
  badgeLabel: string;
}

/**
 * Resolve which hub view to render + how the switcher behaves.
 *  - Admin: a full dropdown (all hubs + "All (advanced)"); selection persisted.
 *  - Matched viewer: their single hub, shown as a static badge (wayfinding).
 *  - Custom viewer/editor: the legacy spine — a zero-regression fallback that
 *    shows everything they're granted — with a static "Custom" badge.
 */
export function resolveHubView(
  role: Role | null,
  allowedModules: string[],
  sel: HubSel | null,
): HubResolution {
  if (role === 'admin') {
    const active: HubSel = sel ?? 'admin';
    const options: HubSel[] = [...HUB_ORDER, ALL_HUB];
    if (active === ALL_HUB) {
      return { mode: 'legacy', active, options, canSwitch: true, badgeLabel: 'All destinations' };
    }
    return { mode: 'hub', hub: HUBS[active], active, options, canSwitch: true, badgeLabel: HUBS[active].label };
  }
  const hk = detectHub(role, allowedModules);
  if (hk === 'custom') {
    return { mode: 'legacy', active: 'admin', options: [], canSwitch: false, badgeLabel: 'Custom' };
  }
  return { mode: 'hub', hub: HUBS[hk], active: hk, options: [hk], canSwitch: false, badgeLabel: `${HUBS[hk].label} hub` };
}

/** Route pathname without any ?view= deep-link — dedupe compares pages, not views. */
const routePath = (to: string): string => to.split('?')[0];

/** Every route the current hub curates (used to de-dupe the "More views" list). */
function hubRoutes(hub: HubDef): Set<string> {
  const s = new Set<string>();
  hub.groups.forEach((g) => g.leaves.forEach((l) => s.add(routePath(l.to))));
  return s;
}

/**
 * Granted destinations NOT surfaced in the current hub — the "More views you
 * can access" safety net, so curation never fully hides something a viewer is
 * entitled to. Drawn from the full rail-nav universe, gated by the viewer's
 * grants, minus what the hub already shows (and minus the Home/Overview
 * anchors, which RailShell always renders).
 */
export function uncuratedLeaves(hub: HubDef, role: Role | null, allowedModules: string[]): RailLeaf[] {
  const shown = hubRoutes(hub);
  const anchors = new Set(['/', '/home']);
  const universe: RailLeaf[] = [...RAIL_SECTIONS.flatMap((s) => s.leaves), ...RAIL_TAIL];
  const seen = new Set<string>();
  const out: RailLeaf[] = [];
  for (const l of universe) {
    // More-views lists PAGES a viewer can reach — ⌘K-only view deep-links
    // (paletteOnly) don't belong here; they'd expand one page into a dozen rows.
    if (l.paletteOnly) continue;
    const p = routePath(l.to);
    if (shown.has(p) || anchors.has(p) || seen.has(p)) continue;
    if (!canSeeNav(l, role, allowedModules)) continue;
    seen.add(p);
    out.push(l);
  }
  return out;
}

// Persist the admin's hub choice per-browser. Kept in its OWN key (not view-as)
// so the nav-only "All (advanced)" mode doesn't perturb the Home composition,
// which still keys off view-as. Falls back to the saved view-as, then Exec.
const HUB_SEL_KEY = 'funnel:hubSel';

export function getHubSel(): HubSel {
  try {
    const v = localStorage.getItem(HUB_SEL_KEY) as HubSel | null;
    if (v === ALL_HUB || (v && HUB_ORDER.includes(v as ViewAs))) return v;
  } catch {
    /* private mode */
  }
  return getViewAs();
}

export function setHubSel(v: HubSel): void {
  try {
    localStorage.setItem(HUB_SEL_KEY, v);
  } catch {
    /* private mode */
  }
}
